/**
 * Terms of Service page. Canonical English source: docs/TERMS_OF_SERVICE.md
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import './LegalPage.css';

const Terms = () => {
  const { isEn, t, lang, localePath } = useLanguage();
  const seo = (
    <Seo
      title={t('legal.termsTitle')}
      description={t('legal.termsDescription')}
      path="/terms"
      lang={lang}
      hreflang
    />
  );

  if (isEn) {
    return (
      <div className="legal-page">
        {seo}
        <div className="container">
          <article className="legal-page__inner">
            <p className="legal-page__eyebrow">Last updated: August 2026</p>
            <h1>Terms of Use</h1>
            <p>
              By creating an account or using ClarCV you accept these terms. If you do not agree,
              do not use the Service.
            </p>

            <h2>1. The Service</h2>
            <p>
              ClarCV provides tools to analyze, improve, and generate CVs with artificial
              intelligence and templates. Results are advisory: you are responsible for reviewing
              content before sending it to employers.
            </p>

            <h2>2. Account</h2>
            <ul>
              <li>Provide accurate information and keep your password secure.</li>
              <li>
                You may sign in with email and password or with Google. Google Sign-In is also
                subject to Google’s terms.
              </li>
              <li>You are responsible for activity under your account.</li>
              <li>Do not use the Service illegally or abusively (e.g. overloading the API).</li>
            </ul>

            <h2>3. Plans and payments</h2>
            <p>
              The Free plan has usage limits. The Pro plan is billed at the published price
              (currently €7/month) via Stripe. You may cancel according to the payment provider’s
              rules; Pro access continues until the end of the paid period unless payment fails.
            </p>

            <h2>4. Your content</h2>
            <p>
              You retain ownership of your CVs.               You grant us a limited license to process them and
              provide the Service (including sending necessary content to AI providers as described
              in the <Link to={localePath('/privacy')}>Privacy Policy</Link>). That content is used only to fulfil
              your request; it is not sold and is not used to train general-purpose AI models.
            </p>

            <h2>5. Limitation of liability</h2>
            <p>
              The Service is provided “as is”. To the extent permitted by law, ClarCV does not
              guarantee job outcomes or uninterrupted availability, and is not liable for indirect
              damages arising from use of the Service.
            </p>

            <h2>6. Governing law</h2>
            <p>
              These terms are governed by the laws of Spain, without prejudice to mandatory consumer
              rights in your country of residence.
            </p>

            <p className="legal-page__note">
              Questions: privacy@clarcv.com. See the <Link to={localePath('/privacy')}>Privacy Policy</Link>.
            </p>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page">
      {seo}
      <div className="container">
        <article className="legal-page__inner">
          <p className="legal-page__eyebrow">Última actualización: agosto 2026</p>
          <h1>Términos de uso</h1>
          <p>
            Al crear una cuenta o usar ClarCV aceptas estos términos. Si no estás de acuerdo,
            no uses el Servicio.
          </p>

          <h2>1. El Servicio</h2>
          <p>
            ClarCV ofrece herramientas para analizar, mejorar y generar currículums con ayuda de
            inteligencia artificial y plantillas. El resultado es orientativo: tú eres responsable
            de revisar el contenido antes de enviarlo a empresas.
          </p>

          <h2>2. Cuenta</h2>
          <ul>
            <li>Debes proporcionar información veraz y mantener segura tu contraseña.</li>
            <li>
              Puedes entrar con correo y contraseña o con Google. El acceso con Google también se
              rige por los términos de Google.
            </li>
            <li>Eres responsable de la actividad realizada con tu cuenta.</li>
            <li>No debes usar el Servicio de forma ilegal o abusiva (p. ej. sobrecargar la API).</li>
          </ul>

          <h2>3. Planes y pagos</h2>
          <p>
            El plan Gratis tiene límites de uso. El plan Pro se factura según el precio publicado
            (actualmente 7 €/mes) a través de Stripe. Puedes cancelar según las condiciones de la
            pasarela de pago; el acceso Pro se mantiene hasta el fin del periodo ya pagado salvo
            fallo de cobro.
          </p>

          <h2>4. Contenido del usuario</h2>
          <p>
            Conservas la titularidad de tus CVs. Nos concedes una licencia limitada para procesarlos
            y prestarte el Servicio (incluido el envío a proveedores de IA según la{' '}
            <Link to={localePath('/privacy')}>Política de privacidad</Link>). Ese contenido se usa solo para tu
            petición: no se vende ni se usa para entrenar modelos de IA de propósito general.
          </p>

          <h2>5. Limitación de responsabilidad</h2>
          <p>
            El Servicio se ofrece “tal cual”. En la medida permitida por la ley, ClarCV no garantiza
            resultados de empleo ni disponibilidad ininterrumpida, y no responde de daños indirectos
            derivados del uso del Servicio.
          </p>

          <h2>6. Ley aplicable</h2>
          <p>
            Estos términos se rigen por la legislación española, sin perjuicio de derechos imperativos
            del consumidor en tu país de residencia.
          </p>

          <p className="legal-page__note">
            Consultas: privacy@clarcv.com. Ver la{' '}
            <Link to={localePath('/privacy')}>Política de privacidad</Link>.
          </p>
        </article>
      </div>
    </div>
  );
};

export default Terms;
